// src/pages/vendor/vendorProfile/FAQ.jsx

import { ArrowLeft } from 'lucide-react';
import BottomNav from '../../../components/layouts/Footer/BottomNav';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

export default function FAQ() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <main className="container mx-auto px-4 md:pb-6 pt-4 pb-20 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl flex items-center gap-3 justify-center sm:text-4xl font-extrabold text-center mb-8 tracking-tight text-gray-900 dark:text-gray-100">
            <ArrowLeft onClick={() => navigate(-1)} className="cursor-pointer size-8" />
            Frequently Asked Questions
          </h1>

          <div className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-base">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">
              Vendor Dashboard - Common Questions
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  How do I manage incoming orders?
                </h3>
                <p>
                  Orders appear on your dashboard with different statuses: Pending, Accepted, Out
                  for Delivery, Delivered, Cancelled, and Rejected. You can filter orders by status
                  and take action on each order. Pending orders require immediate attention for
                  acceptance or rejection.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  How do I update my product prices and availability?
                </h3>
                <p>
                  Go to the Product Pricing page from your dashboard. You can search for products,
                  filter by category, and edit product variants. Click on any product card to open
                  the edit modal where you can update prices, mark products as
                  available/unavailable, and manage product variants. Changes are highlighted in
                  blue before saving.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  How do I set my shop&apos;s online/offline status?
                </h3>
                <p>
                  On your Profile page, you&apos;ll find an &quot;Online Status&quot; toggle. When
                  turned on, your shop is Live and accepting orders. When turned off, your shop goes
                  offline and customers cannot place orders. This is useful when you need to
                  temporarily close your shop.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  How do I configure my shop timings and weekly off days?
                </h3>
                <p>
                  Navigate to Profile → Order Settings. Here you can set your shop&apos;s opening
                  and closing times, configure break hours, select weekly off days, and set delivery
                  preparation times. You can also configure delivery slot intervals for scheduled
                  deliveries.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  How do I update my business profile information?
                </h3>
                <p>
                  Go to Profile → My Profile to update your personal information. For business
                  details like shop name, tagline, and about us section, go to Profile → About
                  Business. You can also add social media links through Profile → Social Media.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  How do I manage my product categories?
                </h3>
                <p>
                  Navigate to Profile → Categories to view and manage your product categories. You
                  can organize your products into different categories to make it easier for
                  customers to browse your offerings.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  How do I add or update my shop&apos;s gallery images?
                </h3>
                <p>
                  Go to Profile → Media to manage your shop&apos;s gallery. You can upload new
                  images showcasing your products, shop interior, or any promotional content. Good
                  quality images help attract more customers.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  How do I update my business address?
                </h3>
                <p>
                  Navigate to Profile → Manage Address. Here you can add or edit your business
                  address, which will be displayed to customers and used for delivery calculations.
                  Make sure your address is accurate for proper order fulfillment.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  What should I do when I receive a new order?
                </h3>
                <p>
                  When a new order comes in, it appears in the Pending section. Review the order
                  details, check product availability, and either accept or reject the order. Once
                  accepted, prepare the order and update the status to &quot;Out for Delivery&quot;
                  when ready for dispatch.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  How do I handle order cancellations?
                </h3>
                <p>
                  If you need to cancel an order after accepting it, you can change the order status
                  to Cancelled. However, try to avoid cancellations as they affect customer
                  experience. If a customer requests cancellation, process it promptly through the
                  order details page.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  Can I set different delivery types?
                </h3>
                <p>
                  Yes, in Order Settings you can configure your delivery preferences including home
                  delivery options, delivery charges, and minimum order values. You can also set
                  delivery slot intervals to offer scheduled delivery options to customers.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  How do I install the vendor app on my device?
                </h3>
                <p>
                  On your Profile page, you&apos;ll find an &quot;Install App&quot; button. Click it
                  to install the BigLocal vendor app on your device for a faster, more integrated
                  experience. The app works offline and provides quick access to your dashboard.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  How do I contact BigLocal support?
                </h3>
                <p>
                  Go to Profile → Contact Support to send us a message or feedback. You can also
                  email us directly at yogesh@revvtch.com for any technical issues, questions, or
                  assistance with your vendor account.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  What are the different order statuses?
                </h3>
                <p>
                  Orders go through several statuses: <strong>Pending</strong> (awaiting your
                  action), <strong>Accepted</strong> (confirmed and being prepared),{' '}
                  <strong>Out for Delivery</strong> (dispatched), <strong>Delivered</strong>{' '}
                  (completed), <strong>Cancelled</strong> (cancelled by customer or vendor), and{' '}
                  <strong>Rejected</strong> (declined by vendor).
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  How do I mark products as out of stock?
                </h3>
                <p>
                  In the Product Pricing page, click on any product to open the edit modal. Toggle
                  the &quot;Available&quot; switch off for products that are out of stock. This will
                  prevent customers from ordering unavailable items. Remember to save your changes.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  Can I set break hours for my shop?
                </h3>
                <p>
                  Yes, in Order Settings you can configure off hours (break times) for your shop.
                  Set the start and end time for your break period, and the system will not accept
                  orders during these hours.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  How do I view my shop as customers see it?
                </h3>
                <p>
                  Navigate to the Store section from your dashboard to see your shop&apos;s customer
                  view. This shows how your products, categories, and shop information appear to
                  customers browsing your store.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  What happens if I&apos;m approved as a vendor?
                </h3>
                <p>
                  Once your vendor account is approved, you can toggle your shop online to start
                  accepting orders. Make sure to complete your profile, add products with accurate
                  pricing, set your shop timings, and configure delivery settings before going live.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  How do I manage product variants (sizes, weights)?
                </h3>
                <p>
                  In the Product Pricing page, each product can have multiple variants (e.g., 500g,
                  1kg). Click on a product to edit its variants individually. You can set different
                  prices and availability for each variant to give customers more options.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700 dark:text-gray-300">
                  Can I filter and search my products?
                </h3>
                <p>
                  Yes, the Product Pricing page has search and filter options. You can search by
                  product name, filter by category, and sort products by various criteria. This
                  makes it easy to manage large product catalogs efficiently.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <BottomNav />
    </>
  );
}
